/** Site drill-down modal for the Labour Management demo screen. Opened by clicking a site
 * name/row anywhere on the page (coverage table, rota table, risk cards). Shows the fuller
 * operational picture for a single site: service periods, roles, clock-in status, overtime,
 * absence, training and labour cost vs budget - see `SiteDrilldown` in `types.ts`. */

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SiteDrilldown } from '../types';
import { applyRoundAndFormat } from '../../../utils';

interface SiteDrilldownDialogProps {
  drilldown: SiteDrilldown | null;
  currencyCode?: string;
  onClose: () => void;
}

const SiteDrilldownDialog = ({ drilldown, currencyCode, onClose }: SiteDrilldownDialogProps) => {
  const { t } = useTranslation('common');

  if (!drilldown) {
    return null;
  }

  const budgetVariance = drilldown.labourCost - drilldown.labourBudget;

  return (
    <Dialog open={Boolean(drilldown)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Box>
            <Typography variant="textLG"
              fontWeight={600}>
              {drilldown.site}
            </Typography>
            <Typography variant="textXS"
              color="text.secondary">
              {`${drilldown.sector} \u00b7 ${drilldown.servicePeriods.join(', ')}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose}
            size="small"
            aria-label={t('labourManagementCloseDialog', 'Close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('labourManagementScheduledRoles', 'Scheduled roles - clock-in status')}
            </Typography>
            {drilldown.scheduledRoles.map((role) => (
              <Stack key={role.role}
                direction="row"
                justifyContent="space-between"
                sx={{ py: 0.5 }}>
                <Typography variant="textSM">{role.role}</Typography>
                <Typography variant="textSM"
                  color={role.clockedIn < role.scheduled ? 'error.main' : 'text.primary'}>
                  {`${role.clockedIn} / ${role.scheduled} clocked in`}
                </Typography>
              </Stack>
            ))}
          </Box>

          <Divider />

          <Box>
            <Stack direction="row"
              justifyContent="space-between">
              <Typography variant="textSM"
                color="text.secondary">{t('labourManagementOpenShifts', 'Open shifts')}</Typography>
              <Typography variant="textSM"
                fontWeight={600}>{drilldown.openShifts}</Typography>
            </Stack>
            <Stack direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}>
              <Typography variant="textSM"
                color="text.secondary">{t('labourManagementLabourCostVsBudget', 'Labour cost vs budget')}</Typography>
              <Typography variant="textSM"
                fontWeight={600}
                color={budgetVariance > 0 ? 'error.main' : 'success.main'}>
                {`${applyRoundAndFormat(drilldown.labourCost, currencyCode)} / ${applyRoundAndFormat(drilldown.labourBudget, currencyCode)}`}
              </Typography>
            </Stack>
          </Box>

          {drilldown.overtimeWarning && (
            <Box>
              <Typography variant="textSM"
                fontWeight={600}
                color="warning.main">
                {t('labourManagementOvertimeWarning', 'Overtime warning')}
              </Typography>
              <Typography variant="textSM">{drilldown.overtimeWarning}</Typography>
            </Box>
          )}

          {drilldown.absenceNotes.length > 0 && (
            <Box>
              <Typography variant="textSM"
                fontWeight={600}>
                {t('labourManagementAbsenceNotes', 'Absence notes')}
              </Typography>
              {drilldown.absenceNotes.map((note) => (
                <Typography key={note}
                  variant="textSM">
                  {`\u2022 ${note}`}
                </Typography>
              ))}
            </Box>
          )}

          {drilldown.trainingIssues.length > 0 && (
            <Box>
              <Typography variant="textSM"
                fontWeight={600}
                color="warning.main">
                {t('labourManagementTrainingIssues', 'Training issues')}
              </Typography>
              {drilldown.trainingIssues.map((issue) => (
                <Typography key={issue}
                  variant="textSM">
                  {`\u2022 ${issue}`}
                </Typography>
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('labourManagementSuggestedActions', 'Suggested actions')}
            </Typography>
            <Stack spacing={1}
              alignItems="flex-start">
              {drilldown.suggestedActions.map((action) => (
                <Button key={action}
                  variant="secondary"
                  size="md">
                  {action}
                </Button>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SiteDrilldownDialog;
